import http from "@/helper/http";
import api from "@/helper/api";

const defaultExpenses = () => ({ items: [], total: 0, page: 1, pages: 0, extras: {} });
const defaultHistory = () => ({ items: [], total: 0, page: 1, pages: 0 });

export default {
  state: {
    expenses: defaultExpenses(),
    expenseTags: [],
    expenseHistory: defaultHistory(),
    expenseDocuments: [],
    rraExpenseNatures: [],
    selectedExpenseMetadata: null,
    loadingExpenses: false,
    loadingExpenseTags: false,
    loadingExpenseHistory: false,
    loadingExpenseDocuments: false,
    loadingRraExpenseNatures: false,
    loadingExpenseMetadata: false,
    loadingCreateExpense: false,
    loadingUpdateExpense: false,
    loadingDeleteExpense: false,
    loadingUploadExpenseDocuments: false,
  },
  mutations: {
    SET_EXPENSES(state, expenses) {
      state.expenses = expenses || defaultExpenses();
    },
    SET_EXPENSE_TAGS(state, tags) {
      state.expenseTags = Array.isArray(tags) ? tags : [];
    },
    SET_EXPENSE_HISTORY(state, history) {
      state.expenseHistory = history || defaultHistory();
    },
    SET_EXPENSE_DOCUMENTS(state, documents) {
      state.expenseDocuments = Array.isArray(documents) ? documents : [];
    },
    SET_RRA_EXPENSE_NATURES(state, natures) {
      state.rraExpenseNatures = Array.isArray(natures) ? natures : [];
    },
    SET_SELECTED_EXPENSE_METADATA(state, metadata) {
      state.selectedExpenseMetadata = metadata || null;
    },
    SET_LOADING_EXPENSES(state, value) {
      state.loadingExpenses = !!value;
    },
    SET_LOADING_EXPENSE_TAGS(state, value) {
      state.loadingExpenseTags = !!value;
    },
    SET_LOADING_EXPENSE_HISTORY(state, value) {
      state.loadingExpenseHistory = !!value;
    },
    SET_LOADING_EXPENSE_DOCUMENTS(state, value) {
      state.loadingExpenseDocuments = !!value;
    },
    SET_LOADING_RRA_EXPENSE_NATURES(state, value) {
      state.loadingRraExpenseNatures = !!value;
    },
    SET_LOADING_EXPENSE_METADATA(state, value) {
      state.loadingExpenseMetadata = !!value;
    },
    SET_LOADING_CREATE_EXPENSE(state, value) {
      state.loadingCreateExpense = !!value;
    },
    SET_LOADING_UPDATE_EXPENSE(state, value) {
      state.loadingUpdateExpense = !!value;
    },
    SET_LOADING_DELETE_EXPENSE(state, value) {
      state.loadingDeleteExpense = !!value;
    },
    SET_LOADING_UPLOAD_EXPENSE_DOCUMENTS(state, value) {
      state.loadingUploadExpenseDocuments = !!value;
    },
    RESET_EXPENSES_STATE(state) {
      state.expenses = defaultExpenses();
    },
    RESET_EXPENSE_DETAIL_STATE(state) {
      state.expenseHistory = defaultHistory();
      state.expenseDocuments = [];
      state.selectedExpenseMetadata = null;
    },
  },
  actions: {
    async listExpenses({ commit }, { storeId, page = 1, per_page = 10, filters = {} }) {
      if (!storeId) {
        commit("RESET_EXPENSES_STATE");
        throw new Error("A storeId is required to list expenses");
      }

      commit("SET_LOADING_EXPENSES", true);
      try {
        const { data } = await http.get(api.store_expenses(storeId), {
          params: { page, per_page, ...filters },
        });
        commit("SET_EXPENSES", data);
        return data;
      } catch (error) {
        commit("RESET_EXPENSES_STATE");
        throw error;
      } finally {
        commit("SET_LOADING_EXPENSES", false);
      }
    },
    async listExpenseTags({ commit }, { storeId }) {
      if (!storeId) {
        commit("SET_EXPENSE_TAGS", []);
        return [];
      }

      commit("SET_LOADING_EXPENSE_TAGS", true);
      try {
        const { data } = await http.get(api.store_tags(storeId));
        const tags = Array.isArray(data) ? data : data?.items || [];
        commit("SET_EXPENSE_TAGS", tags);
        return tags;
      } finally {
        commit("SET_LOADING_EXPENSE_TAGS", false);
      }
    },
    async createTag(_, { payload }) {
      const { data } = await http.post(api.store_tag, payload);
      return data;
    },
    async assignExpenseTag(_, { payload }) {
      const { data } = await http.post(api.store_expense_tag, payload);
      return data;
    },
    async createExpense({ commit }, { payload }) {
      commit("SET_LOADING_CREATE_EXPENSE", true);
      try {
        const { data } = await http.post(api.store_expense, payload);
        return data;
      } finally {
        commit("SET_LOADING_CREATE_EXPENSE", false);
      }
    },
    async updateExpense({ commit }, { payload }) {
      commit("SET_LOADING_UPDATE_EXPENSE", true);
      try {
        const { data } = await http.patch(api.store_expense, payload);
        return data;
      } finally {
        commit("SET_LOADING_UPDATE_EXPENSE", false);
      }
    },
    async deleteExpense({ commit }, { expenseId }) {
      commit("SET_LOADING_DELETE_EXPENSE", true);
      try {
        const { data } = await http.delete(api.store_expense, {
          data: { expense_id: expenseId },
        });
        return data;
      } finally {
        commit("SET_LOADING_DELETE_EXPENSE", false);
      }
    },
    async listExpenseHistory({ commit }, { expenseId, page = 1, per_page = 10 }) {
      commit("SET_LOADING_EXPENSE_HISTORY", true);
      try {
        const { data } = await http.get(api.store_expense_history(expenseId), {
          params: { page, per_page },
        });
        commit("SET_EXPENSE_HISTORY", data);
        return data;
      } finally {
        commit("SET_LOADING_EXPENSE_HISTORY", false);
      }
    },
    async listRraExpenseNatures({ commit, state }) {
      if (state.rraExpenseNatures.length) {
        return state.rraExpenseNatures;
      }

      commit("SET_LOADING_RRA_EXPENSE_NATURES", true);
      try {
        const { data } = await http.get(api.rra_expense_natures);
        commit("SET_RRA_EXPENSE_NATURES", data);
        return data;
      } finally {
        commit("SET_LOADING_RRA_EXPENSE_NATURES", false);
      }
    },
    async getRraExpenseMetadata({ commit }, { storeId, expenseId }) {
      commit("SET_LOADING_EXPENSE_METADATA", true);
      try {
        const { data } = await http.get(api.rra_expense_metadata(storeId, expenseId));
        commit("SET_SELECTED_EXPENSE_METADATA", data);
        return data;
      } catch (error) {
        if (error?.response?.status === 404) {
          commit("SET_SELECTED_EXPENSE_METADATA", null);
          return null;
        }
        throw error;
      } finally {
        commit("SET_LOADING_EXPENSE_METADATA", false);
      }
    },
    async createRraExpenseMetadata(_, { storeId, expenseId, payload }) {
      const { data } = await http.post(
        api.rra_expense_metadata(storeId, expenseId),
        payload,
      );
      return data;
    },
    async updateRraExpenseMetadata(_, { storeId, expenseId, payload }) {
      const { data } = await http.patch(
        api.rra_expense_metadata(storeId, expenseId),
        payload,
      );
      return data;
    },
    async listExpenseDocuments({ commit }, { storeId, expenseId }) {
      commit("SET_LOADING_EXPENSE_DOCUMENTS", true);
      try {
        const { data } = await http.get(api.store_expense_documents(storeId, expenseId));
        const documents = Array.isArray(data) ? data : data?.items || [];
        commit("SET_EXPENSE_DOCUMENTS", documents);
        return documents;
      } finally {
        commit("SET_LOADING_EXPENSE_DOCUMENTS", false);
      }
    },
    async uploadExpenseDocuments({ commit }, { storeId, expenseId, files }) {
      commit("SET_LOADING_UPLOAD_EXPENSE_DOCUMENTS", true);
      try {
        const formData = new FormData();
        Array.from(files || []).forEach((file) => formData.append("documents", file));
        const { data } = await http.post(
          api.store_expense_documents(storeId, expenseId),
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        return data;
      } finally {
        commit("SET_LOADING_UPLOAD_EXPENSE_DOCUMENTS", false);
      }
    },
    async deleteExpenseDocument(_, { storeId, expenseId, docId }) {
      const { data } = await http.delete(
        `${api.store_expense_documents(storeId, expenseId)}/${docId}`,
      );
      return data;
    },
    async downloadExpenseDocument(_, { storeId, filename }) {
      const { data } = await http.get(api.store_expense_document_file(storeId, filename), {
        responseType: "blob",
      });
      return data;
    },
  },
  getters: {
    expenses: (state) => state.expenses,
    expenseTags: (state) => state.expenseTags,
    expenseHistory: (state) => state.expenseHistory,
    expenseDocuments: (state) => state.expenseDocuments,
    rraExpenseNatures: (state) => state.rraExpenseNatures,
    selectedExpenseMetadata: (state) => state.selectedExpenseMetadata,
    loadingExpenses: (state) => state.loadingExpenses,
    loadingExpenseTags: (state) => state.loadingExpenseTags,
    loadingExpenseHistory: (state) => state.loadingExpenseHistory,
    loadingExpenseDocuments: (state) => state.loadingExpenseDocuments,
    loadingRraExpenseNatures: (state) => state.loadingRraExpenseNatures,
    loadingExpenseMetadata: (state) => state.loadingExpenseMetadata,
    loadingCreateExpense: (state) => state.loadingCreateExpense,
    loadingUpdateExpense: (state) => state.loadingUpdateExpense,
    loadingDeleteExpense: (state) => state.loadingDeleteExpense,
    loadingUploadExpenseDocuments: (state) => state.loadingUploadExpenseDocuments,
  },
};
